import {
  CreateProductConfigurationsInput,
  CustomerUserAccountType,
  ProductConfigurations,
} from './graphql/generated';
import { LocaleKey } from './i18n/i18n';
import {
  AppContextType,
  ModuleName,
  OfferContextType,
  OrderFlow,
  TAddress,
  TFormValues,
  TPersonalData,
} from './types';

export const ROUTE_AFTER_LOGIN_KEY = 'route-after-login';
export const ROOT_MODAL_ID = 'root-modal';
export const ACCOUNT_OWNER_MAX_LENGTH = 60;

export const REFRESH_OFFER_EVENT = 'refresh-offer';

const getTodayZeroHours = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const EMPTY_PERSONAL_DATA: TPersonalData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

export const EMPTY_DELIVERY_ADDRESS: TAddress = {
  city: '',
  postalCode: '',
  streetName: '',
  country: 'DE',
  streetNumber: '',
};

export const twentyDays = 20 * 24 * 60 * 60 * 1000;

export const EMPTY_BILLING_ADDRESS: TAddress = {
  city: '',
  postalCode: '',
  streetName: '',
  country: 'DE',
  streetNumber: '',
};

export const defaultFormValues: TFormValues = {
  accountOwner: '',
  confirmedCancellationDate: new Date(),
  contractTerminated: false,
  terminatedEnergyProviderName: undefined,
  personalData: EMPTY_PERSONAL_DATA,
  deliveryAddress: EMPTY_DELIVERY_ADDRESS,
  deliveryStartDate: new Date(getTodayZeroHours().getTime() + twentyDays),
  billingAddress: EMPTY_BILLING_ADDRESS,
  iban: '',
  meterId: '',
  paymentAuthorization: false,
  termsAndConditions: false,
  bindingContract: false,
  marketing: false,
  interestedInHardwareOffer: false,
};

export const DEFAULT_APP_CONTEXT: AppContextType = {
  accountType: CustomerUserAccountType.PrivateCustomer,
  currentBackground: undefined,
  formValues: defaultFormValues,
  offerRequiresManualAddress: false,
  contractCreationBlocked: null,
  orderComplete: false,
  orderFlow: OrderFlow.energyDynamic,
  selectedSite: null,
  setAccountType: () => {},
  setFormValues: () => {},
  setOfferRequiresManualAddress: () => {},
  setContractCreationBlocked: () => {},
  setOrderComplete: () => {},
  setSelectedSite: () => {},
  setUser: () => {},
  setUserProcessing: () => {},
  user: null,
  userProcessing: true,
  modules: new Set<ModuleName>(),
  addModule: () => {},
  removeModule: () => {},
  toggleModule: () => {},
  customBillingAddress: false,
  setCustomBillingAddress: () => {},
  setOrderFlow: () => {},
  currentModal: false,
  setCurrentModal: () => {},
  setCurrentBackground: () => {},
};

export const DEFAULT_PERSONS = 2;

export const PREDEFINED_CONSUMPTION: { [key: string]: number } = {
  1: 1400,
  2: 2300,
  3: 3000,
  4: 3700,
  5: 4400,
  6: 5100,
  7: 5800,
  8: 6500,
};

export const initialOfferInput: CreateProductConfigurationsInput = {
  deliveryAddress: EMPTY_DELIVERY_ADDRESS,
  expectedElectricityConsumptionInKwh: PREDEFINED_CONSUMPTION[2],
  workEmail: '',
};

export const DEFAULT_OFFER_CONTEXT: OfferContextType = {
  offer: null,
  offerLoading: false,
  offerError: null,
  productConfigurationsInput: initialOfferInput,
  setProductConfigurationsInput: () => {},
  personsInHousehold: DEFAULT_PERSONS,
  setPersonsInHousehold: () => {},
  workEmail: '',
  setWorkEmail: () => {},
  setOffer: () => {},
  setOfferLoading: (): void => {},
  setOfferError: (): void => {},
};

export enum AppRoute {
  ROOT = '/',
  ENERGY_STORE = '/energy-store',
  TARIFFS_COMPARISON = '/tariffs-comparison',
  TARIFF_OVERVIEW = '/tariff-overview',
  BATTERY_OPTIMIZATION = '/battery-optimization',
  DATA_ENTRY = '/data-entry',
  DATA_REVIEW = '/data-review',
  ORDER_COMPLETE = '/order-complete',
  LOGIN = '/login',
  LOGOUT = '/logout',
  MAINTENANCE = '/maintenance-work-notice',
  SELF_CONSUMPTION_PLUS = '/self-consumption-plus',
}

export const ROUTE_FLOW: Record<OrderFlow, AppRoute[]> = Object.freeze({
  [OrderFlow.energyDynamic]: [
    AppRoute.ROOT,
    AppRoute.TARIFFS_COMPARISON,
    AppRoute.TARIFF_OVERVIEW,
    AppRoute.DATA_ENTRY,
    AppRoute.DATA_REVIEW,
    AppRoute.ORDER_COMPLETE,
  ],
  [OrderFlow.energyFix]: [
    AppRoute.ROOT,
    AppRoute.TARIFFS_COMPARISON,
    AppRoute.TARIFF_OVERVIEW,
    AppRoute.DATA_ENTRY,
    AppRoute.DATA_REVIEW,
    AppRoute.ORDER_COMPLETE,
  ],
  [OrderFlow.selfConsumptionPlus]: [
    AppRoute.ENERGY_STORE,
    AppRoute.SELF_CONSUMPTION_PLUS,
    AppRoute.DATA_ENTRY,
    AppRoute.DATA_REVIEW,
    AppRoute.ORDER_COMPLETE,
  ],
  [OrderFlow.batteryOptimization]: [
    AppRoute.ENERGY_STORE,
    AppRoute.BATTERY_OPTIMIZATION,
    AppRoute.DATA_ENTRY,
    AppRoute.DATA_REVIEW,
    AppRoute.ORDER_COMPLETE,
  ],
});

export const allRoutes: AppRoute[] = Object.values(AppRoute);

export const skippableRoutes: Set<AppRoute> = new Set([
  AppRoute.TARIFF_OVERVIEW,
  AppRoute.BATTERY_OPTIMIZATION,
  AppRoute.SELF_CONSUMPTION_PLUS,
]);

export const nextLabelByPath: Record<AppRoute, LocaleKey | null> = {
  [AppRoute.TARIFFS_COMPARISON]: 'tariffsComparison.selectTariff',
  [AppRoute.TARIFF_OVERVIEW]: 'offer.offerBtn',
  [AppRoute.BATTERY_OPTIMIZATION]: 'continueOrder',
  [AppRoute.DATA_ENTRY]: 'data.continueAndReview',
  [AppRoute.DATA_REVIEW]: 'review.orderNow',
  [AppRoute.ORDER_COMPLETE]: null,
  [AppRoute.ROOT]: null,
  [AppRoute.LOGIN]: null,
  [AppRoute.LOGOUT]: null,
  [AppRoute.ENERGY_STORE]: null,
  [AppRoute.MAINTENANCE]: null,
  [AppRoute.SELF_CONSUMPTION_PLUS]: 'activateNow',
};

export const noBottomCtaRoutes: AppRoute[] = [
  AppRoute.ROOT,
  AppRoute.TARIFFS_COMPARISON,
  AppRoute.DATA_ENTRY,
  AppRoute.DATA_REVIEW,
  AppRoute.ORDER_COMPLETE,
];

export const modulePropertyByName: Record<
  ModuleName,
  keyof Pick<
    NonNullable<ProductConfigurations['energyDynamic']>,
    'configurationWithBatteryOptimizationModule'
  >
> = {
  [ModuleName.PRICE_OPTIMIZATION]: 'configurationWithBatteryOptimizationModule',
};

export const QUARTERLY_PRICE_CHART_CURRENTLY_HOVERED = 'quarterly-price-chart-hovered';

export enum DataSection {
  DeliveryDetails = 'deliveryDetails',
  PaymentDetails = 'paymentDetails',
}

export const sectionsByFlow: Record<OrderFlow, Set<DataSection>> = Object.freeze({
  [OrderFlow.energyDynamic]: new Set([DataSection.DeliveryDetails, DataSection.PaymentDetails]),
  [OrderFlow.energyFix]: new Set([DataSection.DeliveryDetails, DataSection.PaymentDetails]),
  [OrderFlow.selfConsumptionPlus]: new Set([]),
  [OrderFlow.batteryOptimization]: new Set([DataSection.PaymentDetails]),
});
